import { useState, useEffect, useRef } from 'react';
import { logInfo } from '@lib/logger/logInfo.ts';

export interface UseAudioTestReturn {
  isPlaying: boolean;
  devices: MediaDeviceInfo[];
  progress: number;
  permissionGranted: boolean;
  startAudioTest: () => void;
  stopAudioTest: () => void;
  requestAudioPermissions: () => Promise<void>;
}

const audioTestUrl = new URL('../assets/audio/testSoundAudio.mp3', import.meta.url).href;

export function useAudioTest (audioUrl = audioTestUrl): UseAudioTestReturn {
  const [isPlaying, setIsPlaying] = useState(false);
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  const [permissionGranted, setPermissionGranted] = useState(false);
  const [progress, setProgress] = useState<number>(0);

  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    const audio = new Audio(audioUrl);
    audio.volume = 1.0;
    audio.loop = true;
    audioRef.current = audio;

    const onTimeUpdate = (): void => {
      const duration = audio.duration;
      if (!Number.isFinite(duration) || duration <= 0) {
        setProgress(0);
        return;
      }
      setProgress(audio.currentTime / duration);
    };

    const onEnded = (): void => {
      setIsPlaying(false);
      setProgress(0);
    };

    audio.addEventListener('timeupdate', onTimeUpdate);
    audio.addEventListener('ended', onEnded);

    return () => {
      audio.pause();
      audio.removeEventListener('timeupdate', onTimeUpdate);
      audio.removeEventListener('ended', onEnded);
    };
  }, [audioUrl]);

  const updateAudioDevices = async (): Promise<void> => {
    try {
      const allDevices = await navigator.mediaDevices.enumerateDevices();
      const audioOutputs = allDevices.filter(device => device.kind === 'audiooutput');
      setDevices(audioOutputs);

      const hasLabels = audioOutputs.some(device => device.label !== '');
      setPermissionGranted(hasLabels);
    } catch (error) {
      void logInfo('Error getting audio devices', error as string);
    }
  };

  const requestAudioPermissions = async (): Promise<void> => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach(track => {
        track.stop();
      });
      await updateAudioDevices();
    } catch (error) {
      void logInfo('User deny permissions', error as string);
      await updateAudioDevices();
    }
  };

  useEffect(() => {
    void updateAudioDevices();

    const handleDeviceChange = (): void => {
      void updateAudioDevices();
    };

    navigator.mediaDevices?.addEventListener('devicechange', handleDeviceChange);
    return () => {
      navigator.mediaDevices?.removeEventListener('devicechange', handleDeviceChange);
    };
  }, []);

  const startAudioTest = (): void => {
    if (audioRef.current == null) {
      return;
    }

    audioRef.current.currentTime = 0;
    setProgress(0);

    audioRef.current
      .play()
      .then(() => {
        setIsPlaying(true);
        void logInfo('Audio is playing on device, audio:', audioUrl);
      })
      .catch(err => {
        void logInfo('Error trying to reproduce audio', err as string);
      });
  };

  const stopAudioTest = (): void => {
    if (audioRef.current == null) {
      return;
    }
    audioRef.current.pause();
    audioRef.current.currentTime = 0;
    setIsPlaying(false);
    setProgress(0);
  };

  return {
    isPlaying,
    devices,
    permissionGranted,
    progress,
    startAudioTest,
    stopAudioTest,
    requestAudioPermissions
  };
}